import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { Tabs, Button, Popover, message } from 'antd';
import type { TabsProps } from 'antd';
import { DownloadOutlined, CrownOutlined, CrownFilled, ShareAltOutlined } from '@ant-design/icons';
import { UiContext } from "../contexts/ui";
import { useCapture } from "../hooks/useCapture";
import ShareMenu from "./ShareMenu";
import { exportStore, importStore, resetStore } from "../db/action";
import { useSubscription } from "../subscription/useSubscription";
import { useKeyPress } from "../hooks";
import SettingBackground from "./SettingBackground";
import Persist from "./Persist";
import "./Settings.sass";
import System from "./System";
import SettingWidgets from "./SettingWidgets";
import SubscriptionPanel from "./SubscriptionPanel";
import AuthPanel from "./AuthPanel";
import PremiumStatus from "./PremiumStatus";

import { API } from "../apiUrl";

const messages = defineMessages({
  background: { id: "background", defaultMessage: "Background" },
  widgets: { id: "widgets", defaultMessage: "Widgets" },
  settings: { id: "settings", defaultMessage: "Settings" },
  download: { id: "download", defaultMessage: "Download" },
  subscription: { id: "subscription", defaultMessage: "Premium" },
  subscribed: { id: "settings.subscribed", defaultMessage: "Subscribed" },
  import: { id: "settings.import", defaultMessage: "Import" },
  export: { id: "settings.export", defaultMessage: "Export" },
  reset: { id: "settings.reset", defaultMessage: "Reset" },
  back: { id: "ui.back", defaultMessage: "Back" },
  logout: { id: "auth.logout", defaultMessage: "logout" },
  premiumUntil: { id: "premium.until", defaultMessage: "Premium until {date}" },
  saving: { id: "download.saving", defaultMessage: "Saving..." },
  share: { id: "share", defaultMessage: "Share" },
  shareGenerating: { id: "share.generating", defaultMessage: "Generating image..." },
});

type Section = 'tabs' | 'auth' | 'premium' | 'status';
type Plan = 'month' | 'year';

const Settings: React.FC = () => {
  const intl = useIntl();
  const { settings, toggleSettings } = React.useContext(UiContext);
  const protocol = window.location.protocol;
  const FREE_LIMIT = 10;
  const DOWNLOAD_KEY = 'lumina-download-count';
  const JWT_KEY = 'lumina-jwt';
  const { isPremium, setSubscription: syncSubscription } = useSubscription();
  const [section, setSection] = React.useState<Section>('tabs');
  const [activeTab, setActiveTab] = React.useState(() => {
    try { return localStorage.getItem('lumina-settings-tab') || '1'; } catch { return '1'; }
  });
  const [downloadCount, setDownloadCount] = React.useState(() => {
    try { return parseInt(localStorage.getItem(DOWNLOAD_KEY) || '0', 10); } catch { return 0; }
  });
  const [premiumMeta, setPremiumMeta] = React.useState<{ tier: string; expiresAt: number | null } | null>(null);
  const [loggedInEmail, setLoggedInEmail] = React.useState<string | null>(null);
  const [authChecked, setAuthChecked] = React.useState(false);
  const [pendingPlan, setPendingPlan] = React.useState<Plan | null>(
    () => (localStorage.getItem("lumina-pending-plan") as Plan) || null
  );

  // 跨站退出同步：退出登录后通知 inoneyear（11.aliving.stream）
  const syncLogout = () => {
    if (BUILD_TARGET !== "web") return; // 扩展端不走跨站同步
    try {
      const iframe = document.createElement("iframe");
      iframe.style.display = "none";
      iframe.referrerPolicy = "no-referrer";
      iframe.src = `https://11.aliving.stream/?logout=1&sync_from=lumina`;
      const remove = () => iframe.remove();
      iframe.onload = () => setTimeout(remove, 3000);
      setTimeout(remove, 15000);
      document.body.appendChild(iframe);
    } catch {}
  };

  const isLoggedIn = !!loggedInEmail;

  const isPremiumMetaValid = premiumMeta?.tier === "premium" && (
    premiumMeta.expiresAt === null || premiumMeta.expiresAt * 1000 > Date.now()
  );
  const isPremiumUser = isPremium || isPremiumMetaValid;
  const downloadLimited = !isPremiumUser && downloadCount >= FREE_LIMIT;

  const { capture, capturing } = useCapture();
  const [shareOpen, setShareOpen] = React.useState(false);
  const [shareBlob, setShareBlob] = React.useState<Blob | null>(null);
  const sharingRef = React.useRef(false);

  const getVerseShareText = (): string => {
    try {
      const textEl = document.querySelector(".verse-cinematic__text");
      const refEl = document.querySelector(".verse-cinematic__ref");
      const text = textEl?.textContent?.trim() || "";
      const ref = refEl?.textContent?.replace(/^—\s*/, "").trim() || "";
      return ref ? `${text} — ${ref}` : text;
    } catch {
      return "";
    }
  };

  const isExtension = BUILD_TARGET !== "web";
  const [checkoutPolling, setCheckoutPolling] = React.useState(false);

  const doCheckout = async (plan: Plan) => {
    const token = localStorage.getItem(JWT_KEY);
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ plan, platform: isExtension ? "extension" : "web" }),
      });
      const data = await res.json();
      if (data.url) {
        if (isExtension) {
          // Extension: open checkout in a new tab so the panel stays alive.
          // After payment, poll /api/auth/me until the subscription updates.
          window.open(data.url, "_blank");
          setCheckoutPolling(true);
          let attempts = 0;
          const poll = setInterval(async () => {
            attempts++;
            try {
              const r = await fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
              if (r.ok) {
                const me = await r.json();
                if (me.subscription?.tier === "premium") {
                  clearInterval(poll);
                  setCheckoutPolling(false);
                  setPremiumMeta({ tier: me.subscription.tier, expiresAt: me.subscription.expires_at });
                  syncSubscription({ tier: me.subscription.tier, expiresAt: me.subscription.expires_at, stripeCustomerId: null });
                  setSection("status");
                  if (!settings) toggleSettings();
                  message.success(intl.formatMessage({ id: "sub.subscribedSuccess", defaultMessage: "Subscription Successful" }));
                }
              }
            } catch {}
            if (attempts >= 30) {
              clearInterval(poll);
              setCheckoutPolling(false);
            }
          }, 2000);
        } else {
          window.location.href = data.url;
        }
      }
    } catch {}
  };

  // Check auth + subscription status on mount
  React.useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem(JWT_KEY);
      if (!token) { setAuthChecked(true); return; }
      try {
        const res = await fetch(`${API}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setLoggedInEmail(data.user?.email || null);
          if (data.subscription?.tier === "premium") {
            setPremiumMeta({ tier: data.subscription.tier, expiresAt: data.subscription.expires_at });
            syncSubscription({ tier: data.subscription.tier, expiresAt: data.subscription.expires_at, stripeCustomerId: null });
            localStorage.removeItem("lumina-pending-plan");
            setPendingPlan(null);
          } else {
            setPremiumMeta(null);
            syncSubscription({ tier: "free", expiresAt: null, stripeCustomerId: null });
            // Auto-checkout pending plan after login (e.g., Google OAuth redirect)
            const savedPlan = localStorage.getItem("lumina-pending-plan") as Plan | null;
            if (savedPlan) {
              localStorage.removeItem("lumina-pending-plan");
              setPendingPlan(null);
              doCheckout(savedPlan);
            }
          }
        }
      } catch {}
      setAuthChecked(true);
    };
    checkAuth();

    // Handle Google OAuth callback redirect
    const url = new URL(window.location.href);
    const tokenParam = url.searchParams.get("token");
    const checkoutId = url.searchParams.get("checkout_id");
    if (tokenParam) {
      localStorage.setItem(JWT_KEY, tokenParam);
      window.history.replaceState({}, "", "/");
      onLoggedIn();
    } else if (checkoutId) {
      const token = localStorage.getItem(JWT_KEY);
      if (token) {
        fetch(`${API}/api/checkout/verify?checkout_id=${checkoutId}`, { headers: { Authorization: `Bearer ${token}` } })
          .then((r) => r.json())
          .then((data) => {
            if (data.subscription?.tier === "premium") {
              setPremiumMeta({ tier: data.subscription.tier, expiresAt: data.subscription.expires_at });
              syncSubscription({ tier: data.subscription.tier, expiresAt: data.subscription.expires_at, stripeCustomerId: null });
              setSection('status');
              if (!settings) toggleSettings();
            }
            window.history.replaceState({}, "", "/");
          });
      }
    }
  }, []);

  const onLoggedIn = () => {
    const planToCheckout = pendingPlan;
    setPendingPlan(null);
    setAuthChecked(false);
    const token = localStorage.getItem(JWT_KEY);
    if (token) {
      fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((data) => {
          setLoggedInEmail(data.user?.email || null);
          if (data.subscription?.tier === "premium") {
            setPremiumMeta({ tier: data.subscription.tier, expiresAt: data.subscription.expires_at });
            syncSubscription({ tier: data.subscription.tier, expiresAt: data.subscription.expires_at, stripeCustomerId: null });
            setSection('status');
            localStorage.removeItem("lumina-pending-plan");
            setPendingPlan(null);
          } else {
            setSection('premium');
            // If user selected a plan before login, auto-checkout now
            const savedPlan = (localStorage.getItem("lumina-pending-plan") as Plan) || planToCheckout;
            if (savedPlan) {
              localStorage.removeItem("lumina-pending-plan");
              setPendingPlan(null);
              doCheckout(savedPlan);
            }
          }
          setAuthChecked(true);
        });
    }
  };

  const currentSection = section;

  const handleReset = () => {
    if (confirm("Are you sure you want to delete all of your LuminaVerse settings?")) resetStore();
  };

  const handleExport = () => {
    const json = exportStore();
    const url = URL.createObjectURL(new Blob([json], { type: "application/json" }));
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.style.display = "none";
    a.href = url;
    a.download = "luminaverse.json";
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const handleImport = () => {
    const input = document.createElement("input");
    document.body.appendChild(input);
    input.style.display = "none";
    input.type = "file";
    input.addEventListener("change", function () {
      if (this.files) {
        const reader = new FileReader();
        reader.addEventListener("load", (e) => {
          if (e.target?.result) {
            try { importStore(JSON.parse(e.target.result as string)); } catch {}
          }
        });
        reader.readAsText(this.files[0]);
      }
      document.body.removeChild(input);
    });
    input.click();
  };

  const handleDownload = async () => {
    if (capturing) return;

    // Block download entirely when limit reached — show auth/premium only
    if (downloadLimited) {
      if (!isLoggedIn) {
        setSection('auth');
      } else if (isPremiumMetaValid) {
        setSection('status');
      } else {
        setSection('premium');
      }
      return;
    }

    // Increment counter for free (non-premium) users only
    if (!isPremiumUser) {
      const next = downloadCount + 1;
      setDownloadCount(next);
      try { localStorage.setItem(DOWNLOAD_KEY, String(next)); } catch {}
    }

    try {
      const { blob } = await capture();
      const url = URL.createObjectURL(blob);

      // Fire-and-forget: track download for abuse detection
      const trackToken = localStorage.getItem(JWT_KEY);
      if (trackToken) {
        fetch(`${API}/api/download/track`, {
          method: "POST",
          headers: { Authorization: `Bearer ${trackToken}` },
        }).catch(() => {});
      }

      const link = document.createElement("a");
      link.href = url;
      link.download = "screenshot.png";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("导出图片失败:", error);
    }
  };

  const handleShare = async () => {
    if (capturing) return;

    // Same limit logic as download
    if (downloadLimited) {
      if (!isLoggedIn) {
        setSection('auth');
      } else if (isPremiumMetaValid) {
        setSection('status');
      } else {
        setSection('premium');
      }
      return;
    }

    // Increment counter for free (non-premium) users only
    if (!isPremiumUser) {
      const next = downloadCount + 1;
      setDownloadCount(next);
      try { localStorage.setItem(DOWNLOAD_KEY, String(next)); } catch {}
    }

    try {
      const { blob } = await capture();

      // Fire-and-forget: track for abuse detection
      const trackToken = localStorage.getItem(JWT_KEY);
      if (trackToken) {
        fetch(`${API}/api/download/track`, {
          method: "POST",
          headers: { Authorization: `Bearer ${trackToken}` },
        }).catch(() => {});
      }

      setShareBlob(blob);
      setShareOpen(true);
    } catch (error) {
      console.error("Share capture failed:", error);
    }
  };

  const tabLabel = (text: string) => (
    <span style={{ display: "inline-block", maxWidth: "22vw", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle" }}>
      {text}
    </span>
  );

  const items: TabsProps['items'] = [
    {
      key: '1', label: tabLabel(intl.formatMessage(messages.background)), children: <SettingBackground />,
    },
    {
      key: '2', label: tabLabel(intl.formatMessage(messages.widgets)), children: <SettingWidgets />,
    },
    {
      key: '3', label: tabLabel(intl.formatMessage(messages.settings)),
      children: <>
        <System />
        <p style={{ marginBottom: "2rem" }}>
          <a onClick={handleImport}>{intl.formatMessage(messages.import)}</a>,{" "}
          <a onClick={handleExport}>{intl.formatMessage(messages.export)}</a>{" "}
          {intl.formatMessage({ id: 'settings.or', defaultMessage: 'or' })}{" "}
          <a onClick={handleReset}>{intl.formatMessage(messages.reset)}</a>{" "}
          {intl.formatMessage({ id: 'settings.yourSettings', defaultMessage: 'your settings' })}
        </p>
        { protocol.startsWith('http') ? null : <Persist /> }
      </>,
    },
  ];

  const extensionPromo = !isExtension && (
    <a
      href="https://chromewebstore.google.com/detail/ecbohdaobhoepfnfimgheaonmlgdhfjd?utm_source=item-share-cb"
      target="_blank"
      rel="noreferrer"
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginTop: "1.5rem",
        padding: "10px 14px",
        borderRadius: 10,
        background: "linear-gradient(135deg, #f0f4ff 0%, #e8f0fe 100%)",
        border: "1px solid #c2d6ff",
        textDecoration: "none",
        color: "inherit",
      }}
    >
      <span
        style={{
          flexShrink: 0,
          width: 36,
          height: 36,
          borderRadius: 8,
          background: "linear-gradient(135deg, #4285f4 0%, #34a853 40%, #fbbc04 70%, #ea4335 100%)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 18,
          color: "#fff",
          fontWeight: 700,
        }}
      >
        C
      </span>
      <div style={{ minWidth: 0, textAlign: "left" }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a1a", lineHeight: 1.3 }}>
          Get Chrome Extension
        </div>
        <div style={{ fontSize: 11, color: "#666", marginTop: 1, lineHeight: 1.3 }}>
          Faster loading · Offline support · Always on your new tab
        </div>
      </div>
    </a>
  );

  const termsLink = (
    <p style={{ marginTop: !isExtension ? 12 : "1.5rem", textAlign: "center" }}>
      <a href={API + "/terms"} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#bbb" }}>
        Terms of Service
      </a>
      {" · "}
      <a href={API + "/privacy"} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#bbb" }}>
        Privacy Policy
      </a>
    </p>
  );

  useKeyPress(toggleSettings, ["Escape"]);

  const expiryText = premiumMeta?.expiresAt
    ? new Date(premiumMeta.expiresAt * 1000).toLocaleDateString()
    : null;

  return (
    <div className="Settings" style={{ margin: 0, padding: "0 8px" }}>
      <div>
        <div style={{ marginBottom: 8, marginTop: 6 }}>
          {/* Row 1: user info */}
          {isLoggedIn && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6, minWidth: 0 }}>
              <span style={{ fontSize: 12, color: "#888", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                {loggedInEmail}
                {isPremiumMetaValid && expiryText && (
                  <span style={{ color: "#aaa" }}>
                    {' · '}{intl.formatMessage(messages.premiumUntil, { date: expiryText })}
                  </span>
                )}
              </span>
              <a
                onClick={() => {
                  localStorage.removeItem(JWT_KEY);
                  setLoggedInEmail(null);
                  setPremiumMeta(null);
                  syncSubscription({ tier: "free", expiresAt: null, stripeCustomerId: null });
                  syncLogout();
                  setSection('tabs');
                }}
                style={{ fontSize: 11, color: "#bbb", flexShrink: 0, marginLeft: 8 }}
              >
                {intl.formatMessage(messages.logout)}
              </a>
            </div>
          )}
          {/* Row 2: action buttons */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              loading={capturing && !shareOpen}
              disabled={capturing}
            >
              <span style={{ marginLeft: 4 }}>{capturing && !shareOpen ? intl.formatMessage(messages.saving) : intl.formatMessage(messages.download)}</span>
            </Button>
            <Popover
              open={shareOpen}
              onOpenChange={(open) => { if (!open && !sharingRef.current) setShareOpen(false); }}
              trigger="click"
              content={<ShareMenu blob={shareBlob} verseText={getVerseShareText()} sharingRef={sharingRef} onClose={() => setShareOpen(false)} />}
            >
              <Button
                size="small"
                icon={<ShareAltOutlined />}
                onClick={handleShare}
                loading={capturing && shareOpen}
                disabled={capturing}
              >
                <span style={{ marginLeft: 4 }}>{capturing && shareOpen ? intl.formatMessage(messages.shareGenerating) : intl.formatMessage(messages.share)}</span>
              </Button>
            </Popover>
            {currentSection !== 'premium' && currentSection !== 'status' && (
              <Button
                size="small"
                icon={isPremiumUser ? <CrownFilled /> : <CrownOutlined />}
                onClick={() => setSection(isPremiumUser ? 'status' : 'premium')}
              >
                {isPremiumUser ? intl.formatMessage(messages.subscribed) : intl.formatMessage(messages.subscription)}
              </Button>
            )}
            {currentSection !== 'tabs' && (
              <Button size="small" onClick={() => setSection('tabs')}>
                {intl.formatMessage(messages.back)}
              </Button>
            )}
          </div>
        </div>

        {currentSection === 'auth' && <AuthPanel onLoggedIn={onLoggedIn} />}
        {currentSection === 'premium' && (
          <SubscriptionPanel
            onRequireLogin={(plan) => {
              localStorage.setItem("lumina-pending-plan", plan);
              setPendingPlan(plan);
              setSection('auth');
            }}
            onCheckout={doCheckout}
            checkoutLoading={checkoutPolling}
            premiumExpiresAt={premiumMeta?.expiresAt}
            premiumTier={premiumMeta?.tier}
            isLoggedIn={isLoggedIn}
          />
        )}
        {currentSection === 'status' && premiumMeta && (
          <PremiumStatus
            tier={premiumMeta.tier}
            expiresAt={premiumMeta.expiresAt}
            isLoggedIn={isLoggedIn}
            onCancelled={() => {
              const token = localStorage.getItem(JWT_KEY);
              if (token) {
                fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
                  .then((r) => r.json())
                  .then((data) => {
                    if (data.subscription) {
                      setPremiumMeta({
                        tier: data.subscription.tier,
                        expiresAt: data.subscription.expires_at,
                      });
                      syncSubscription({
                        tier: data.subscription.tier,
                        expiresAt: data.subscription.expires_at,
                        stripeCustomerId: null,
                      });
                    }
                  })
                  .catch(() => {});
              }
            }}
          />
        )}
        {currentSection === 'tabs' && (
          <Tabs
            activeKey={activeTab}
            onChange={(key) => {
              setActiveTab(key);
              try { localStorage.setItem('lumina-settings-tab', key); } catch {}
            }}
            items={items}
          />
        )}
        {extensionPromo}
        {termsLink}
      </div>
    </div>
  );
};

export default React.memo(Settings);
